import {
  createContext,
  useContext as useReactContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { PromiseWithServerResult, Zero } from "@rocicorp/zero";
import { ReorderableArray } from "@corpuscomplete/core-ui/form/reorderable-array";
import type { FoodItemMatcher } from "@corpuscomplete/core/nutrition/food-matcher";
import { PlannedMeal } from "@corpuscomplete/core/nutrition/planned-meal";
import type { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { createId, nSortIndexesBetween, sort, sortIndexBetween } from "@corpuscomplete/core/utils";
import { CurrentActor } from "../../../actor";

type Meal = PlannedMeal.Info;
type Plate = Meal["plates"][number];
type PlateItem = Plate["items"][number];
type PlateDescriptor = Plate["itemDescriptors"][number];
type Event = Meal["events"][number];
type ReorderPlateEntryArgs = {
  activeId: string;
  dropTarget: ReorderableArray.DropTarget | null;
  entries: readonly PlannedMealDetail.MergedPlateEntry[];
};
type SinglePlateEntryReorder = {
  type: "single";
  entry: PlannedMealDetail.MergedPlateEntry;
  sortIndex: string;
};
type RepairPlateEntryReorder = {
  type: "repair";
  itemEdits: Array<{ id: string; sortIndex: string }>;
  descriptorEdits: Array<{ id: string; sortIndex: string }>;
};

export namespace PlannedMealDetail {
  type Context = {
    mealId: string;
  };

  const DataContext = createContext<Context | null>(null);

  export type LoadingState = {
    isLoading: boolean;
  };

  export type PublishViolation = {
    key: string;
    target: PlannedMeal.PublishViolationTarget;
    message: string;
  };

  export type MergedPlateEntry =
    | { id: string; type: "item"; sortIndex: string; item: PlateItem }
    | { id: string; type: "descriptor"; sortIndex: string; descriptor: PlateDescriptor };

  export type DetailMutations = {
    rename: (name: string) => PromiseWithServerResult;
    publish: () => PromiseWithServerResult;
    unpublish: () => PromiseWithServerResult;
    deleteDraft: () => PromiseWithServerResult;
    addPlate: (input: {
      userId: string;
      nutrientTargets?: Nutrient.Targets | null;
    }) => PromiseWithServerResult;
    addEvent: (input: {
      timeAt: Temporal.Instant;
      timeZone: string;
      id?: string;
    }) => PromiseWithServerResult;
  };

  export type PlateMutations = {
    editTargets: (nutrientTargets: Nutrient.Targets | null) => PromiseWithServerResult;
    removePlate: () => PromiseWithServerResult;
    addItem: (input: {
      foodServingId: string;
      amount: number;
      id?: string;
      sortIndex?: string;
    }) => PromiseWithServerResult;
    editItem: (
      id: string,
      patch: Partial<Pick<PlateItem, "amount" | "foodServingId" | "sortIndex">>,
    ) => PromiseWithServerResult;
    reorderItem: (input: {
      activeId: string;
      dropTarget: ReorderableArray.DropTarget | null;
    }) => PromiseWithServerResult | undefined;
    removeItem: (id: string) => PromiseWithServerResult;
    addDescriptor: (input: {
      data: FoodItemMatcher.ItemDescriptor;
      id?: string;
      sortIndex?: string;
    }) => PromiseWithServerResult;
    editDescriptor: (
      id: string,
      patch: Partial<Pick<PlateDescriptor, "data" | "sortIndex">>,
    ) => PromiseWithServerResult;
    removeDescriptor: (id: string) => PromiseWithServerResult;
  };

  export type EventMutations = {
    editEvent: (input: {
      timeAt?: Temporal.Instant;
      timeZone?: string;
    }) => PromiseWithServerResult | undefined;
    removeEvent: () => PromiseWithServerResult | undefined;
    addAssignment: (plateId: string) => PromiseWithServerResult | undefined;
    removeAssignment: (assignmentId: string) => PromiseWithServerResult | undefined;
  };

  export function preload(zero: Zero, mealId: string): void {
    void zero.run(queries.plannedMeal.byId({ id: mealId }));
  }

  export function noopMutationResult(): PromiseWithServerResult {
    const success = Promise.resolve({ type: "success" as const });
    return { client: success, server: success };
  }

  export function Provider({ mealId, children }: PropsWithChildren<{ mealId: string }>) {
    const value = useMemo(() => ({ mealId }), [mealId]);
    return <DataContext value={value}>{children}</DataContext>;
  }

  function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error("PlannedMealDetail hooks must be used within PlannedMealDetail.Provider");
    }
    return context;
  }

  export function useMeal(): Meal | undefined {
    const { mealId } = useContext();
    const [meal] = useQuery(queries.plannedMeal.byId({ id: mealId }));
    return meal ? PlannedMeal.asInfo(meal) : undefined;
  }

  export function useLoadingState(): LoadingState {
    const { mealId } = useContext();
    const [, details] = useQuery(queries.plannedMeal.byId({ id: mealId }));
    return { isLoading: details.type === "unknown" };
  }

  export function usePublishViolations(): PublishViolation[] {
    const meal = useMeal();
    return useMemo(() => (meal ? publishViolations(meal) : []), [meal]);
  }

  export function useMutations(): DetailMutations {
    const { mealId } = useContext();
    const z = useZero();
    return useMemo(
      () => ({
        rename: (name) => z.mutate(mutators.plannedMeal.editDraft({ id: mealId, name })),
        publish: () => z.mutate(mutators.plannedMeal.publish({ id: mealId })),
        unpublish: () => z.mutate(mutators.plannedMeal.unpublish({ id: mealId })),
        deleteDraft: () => z.mutate(mutators.plannedMeal.deleteDraft({ id: mealId })),
        addPlate: ({ userId, nutrientTargets = null }) =>
          z.mutate(
            mutators.plannedMeal.addPlate({
              mealId,
              id: createId(),
              userId,
              nutrientTargets,
              items: [],
              itemDescriptors: [],
            }),
          ),
        addEvent: ({ id = createId(), timeAt, timeZone }) =>
          z.mutate(mutators.plannedMeal.addEvent({ mealId, id, timeAt, timeZone })),
      }),
      [mealId, z],
    );
  }

  export function usePlateMutations(plateId: string): PlateMutations {
    const { mealId } = useContext();
    const meal = useMeal();
    const z = useZero();
    const plate = meal?.plates.find((candidate) => candidate.id === plateId);

    return useMemo(
      () => ({
        editTargets: (nutrientTargets) =>
          z.mutate(mutators.plannedMeal.editPlate({ mealId, id: plateId, nutrientTargets })),
        removePlate: () => z.mutate(mutators.plannedMeal.removePlate({ mealId, id: plateId })),
        addItem: ({ id = createId(), foodServingId, amount, sortIndex }) =>
          z.mutate(
            mutators.plannedMeal.addPlateItem({
              mealId,
              plateId,
              id,
              foodServingId,
              amount,
              sortIndex: sortIndex ?? nextSortIndex(plate),
            }),
          ),
        editItem: (id, patch) =>
          z.mutate(mutators.plannedMeal.editPlateItem({ mealId, plateId, id, ...patch })),
        reorderItem: ({ activeId, dropTarget }) => {
          const entries = plate ? mergedPlateEntries(plate) : null;
          if (!entries) return undefined;

          const reorder = planPlateEntryReorder({
            activeId,
            dropTarget,
            entries,
          });
          if (!reorder) return undefined;
          if (reorder.type === "single") {
            if (reorder.entry.type === "item") {
              return z.mutate(
                mutators.plannedMeal.editPlateItem({
                  mealId,
                  plateId,
                  id: reorder.entry.id,
                  sortIndex: reorder.sortIndex,
                }),
              );
            }

            return z.mutate(
              mutators.plannedMeal.editPlateItemDescriptor({
                mealId,
                plateId,
                id: reorder.entry.id,
                sortIndex: reorder.sortIndex,
              }),
            );
          }

          if (reorder.itemEdits.length === 0 && reorder.descriptorEdits.length === 0) {
            return undefined;
          }

          return z.mutate(
            mutators.plannedMeal.editPlate({
              mealId,
              id: plateId,
              ...(reorder.itemEdits.length > 0 ? { items: { edit: reorder.itemEdits } } : {}),
              ...(reorder.descriptorEdits.length > 0
                ? { itemDescriptors: { edit: reorder.descriptorEdits } }
                : {}),
            }),
          );
        },
        removeItem: (id) => z.mutate(mutators.plannedMeal.removePlateItem({ mealId, plateId, id })),
        addDescriptor: ({ id = createId(), data, sortIndex }) =>
          z.mutate(
            mutators.plannedMeal.addPlateItemDescriptor({
              mealId,
              plateId,
              id,
              data,
              sortIndex: sortIndex ?? nextSortIndex(plate),
            }),
          ),
        editDescriptor: (id, patch) =>
          z.mutate(mutators.plannedMeal.editPlateItemDescriptor({ mealId, plateId, id, ...patch })),
        removeDescriptor: (id) =>
          z.mutate(mutators.plannedMeal.removePlateItemDescriptor({ mealId, plateId, id })),
      }),
      [mealId, plate, plateId, z],
    );
  }

  export function useEventMutations(eventId?: string): EventMutations {
    const { mealId } = useContext();
    const z = useZero();

    return useMemo(
      () => ({
        editEvent: ({ timeAt, timeZone }) =>
          eventId
            ? z.mutate(
                mutators.plannedMeal.editEvent({
                  mealId,
                  id: eventId,
                  ...(timeAt ? { timeAt } : {}),
                  ...(timeZone ? { timeZone } : {}),
                }),
              )
            : undefined,
        removeEvent: () =>
          eventId ? z.mutate(mutators.plannedMeal.removeEvent({ mealId, id: eventId })) : undefined,
        addAssignment: (plateId) =>
          eventId
            ? z.mutate(
                mutators.plannedMeal.addAssignment({
                  mealId,
                  id: createId(),
                  eventId,
                  plateId,
                }),
              )
            : undefined,
        removeAssignment: (id) => z.mutate(mutators.plannedMeal.removeAssignment({ mealId, id })),
      }),
      [eventId, mealId, z],
    );
  }

  export function currentUserPlate(meal: Meal, userId: string): Plate | undefined {
    return meal.plates.find((plate) => plate.userId === userId);
  }

  export function otherPlates(meal: Meal, userId: string): Plate[] {
    return meal.plates.filter((plate) => plate.userId !== userId);
  }

  export function plateItemCount(plate: Pick<Plate, "items">): number {
    return plate.items.length;
  }

  export function plateDescriptorCount(plate: Pick<Plate, "itemDescriptors">): number {
    return plate.itemDescriptors.length;
  }

  export function canEditContent(meal: Pick<Meal, "status">): boolean {
    return meal.status === "draft";
  }

  export function canEditSchedule(_meal: Pick<Meal, "status">): boolean {
    return _meal.status === "draft";
  }

  export function mergedPlateEntries(plate: Plate): readonly MergedPlateEntry[] {
    return sort([
      ...plate.items.map((item) => ({
        id: item.id,
        type: "item" as const,
        sortIndex: item.sortIndex,
        item,
      })),
      ...plate.itemDescriptors.map((descriptor) => ({
        id: descriptor.id,
        type: "descriptor" as const,
        sortIndex: descriptor.sortIndex,
        descriptor,
      })),
    ]);
  }

  export function publishViolations(meal: Meal): PublishViolation[] {
    const violations = PlannedMeal.publishViolations(meal);
    if (!violations) return [];

    const result: PublishViolation[] = [];
    if (violations.plannedMeal.emptyName) {
      result.push({
        key: "plannedMeal:emptyName",
        target: { entity: "plannedMeal", code: "emptyName" },
        message: "Name is required before publishing.",
      });
    }
    if (violations.plannedMeal.noPlates) {
      result.push({
        key: "plannedMeal:noPlates",
        target: { entity: "plannedMeal", code: "noPlates" },
        message: "At least one plate is required before publishing.",
      });
    }
    if (violations.plannedMeal.noEvents) {
      result.push({
        key: "plannedMeal:noEvents",
        target: { entity: "plannedMeal", code: "noEvents" },
        message: "At least one event is required before publishing.",
      });
    }
    for (const [eventId, eventViolations] of Object.entries(violations.events)) {
      if (eventViolations.noAssignments) {
        result.push({
          key: `event:${eventId}:noAssignments`,
          target: { entity: "event", id: eventId, code: "noAssignments" },
          message: "Every event needs at least one assignment.",
        });
      }
    }
    for (const [plateId, plateViolations] of Object.entries(violations.plates)) {
      if (plateViolations.noItems) {
        result.push({
          key: `plate:${plateId}:noItems`,
          target: { entity: "plate", id: plateId, code: "noItems" },
          message: "Every plate needs at least one food item.",
        });
      }
      if (plateViolations.hasDescriptors) {
        result.push({
          key: `plate:${plateId}:hasDescriptors`,
          target: { entity: "plate", id: plateId, code: "hasDescriptors" },
          message: "Resolve or remove descriptors before publishing.",
        });
      }
      if (plateViolations.noAssignments) {
        result.push({
          key: `plate:${plateId}:noAssignments`,
          target: { entity: "plate", id: plateId, code: "noAssignments" },
          message: "Every plate needs at least one event assignment.",
        });
      }
    }
    return result;
  }

  export function eventSummary(event: Event): string {
    const zoned = event.timeAt.toZonedDateTimeISO(event.timeZone);
    return `${zoned.toPlainDate().toString()} ${zoned.toPlainTime().toString({ smallestUnit: "minute" })}`;
  }

  export function hasLinkedFoodLogAssignments(meal: Pick<Meal, "events">): boolean {
    return meal.events.some((event) =>
      event.assignments.some((assignment) => assignment.foodLogSourceGroup),
    );
  }

  export function useCurrentUserPlate(): Plate | undefined {
    const meal = useMeal();
    const actor = CurrentActor.use();
    return meal ? currentUserPlate(meal, actor.userId) : undefined;
  }

  export function useOtherPlates(): Plate[] {
    const meal = useMeal();
    const actor = CurrentActor.use();
    return useMemo(() => (meal ? otherPlates(meal, actor.userId) : []), [actor.userId, meal]);
  }

  function nextSortIndex(plate: Plate | undefined): string {
    const entries = plate ? mergedPlateEntries(plate) : [];
    const last = entries.length > 0 ? entries[entries.length - 1]?.sortIndex : null;
    return sortIndexBetween(last ?? null, null);
  }

  function planPlateEntryReorder(
    args: ReorderPlateEntryArgs,
  ): SinglePlateEntryReorder | RepairPlateEntryReorder | null {
    const reorderedEntries = getReorderedPlateEntries(args);
    if (!reorderedEntries) return null;

    const singleEntryReorder = getSinglePlateEntryReorder(reorderedEntries, args.activeId);
    if (singleEntryReorder) return singleEntryReorder;

    return getRepairPlateEntryReorder(reorderedEntries);
  }

  function getReorderedPlateEntries({
    activeId,
    dropTarget,
    entries,
  }: ReorderPlateEntryArgs): readonly MergedPlateEntry[] | null {
    if (!dropTarget) return null;

    const activeEntry = entries.find((entry) => entry.id === activeId);
    if (!activeEntry) return null;

    const remainingEntries = entries.filter((entry) => entry.id !== activeId);
    const nextIndex =
      dropTarget.nextId === null
        ? remainingEntries.length
        : remainingEntries.findIndex((entry) => entry.id === dropTarget.nextId);
    if (nextIndex === -1) return null;

    const reorderedEntries = [
      ...remainingEntries.slice(0, nextIndex),
      activeEntry,
      ...remainingEntries.slice(nextIndex),
    ];

    return entries.every((entry, index) => entry.id === reorderedEntries[index]?.id)
      ? null
      : reorderedEntries;
  }

  function getSinglePlateEntryReorder(
    reorderedEntries: readonly MergedPlateEntry[],
    activeId: string,
  ): SinglePlateEntryReorder | null {
    const nextIndex = reorderedEntries.findIndex((entry) => entry.id === activeId);
    const entry = reorderedEntries[nextIndex];
    if (!entry) return null;

    const prev = reorderedEntries[nextIndex - 1];
    const next = reorderedEntries[nextIndex + 1];
    if (prev?.sortIndex && next?.sortIndex && prev.sortIndex === next.sortIndex) return null;

    const sortIndex = sortIndexBetween(prev?.sortIndex ?? null, next?.sortIndex ?? null);
    if (entry.sortIndex === sortIndex) return null;

    return { type: "single", entry, sortIndex };
  }

  function getRepairPlateEntryReorder(
    reorderedEntries: readonly MergedPlateEntry[],
  ): RepairPlateEntryReorder {
    const nextSortIndexes = nSortIndexesBetween(null, null, reorderedEntries.length);
    const itemEdits: RepairPlateEntryReorder["itemEdits"] = [];
    const descriptorEdits: RepairPlateEntryReorder["descriptorEdits"] = [];

    reorderedEntries.forEach((entry, index) => {
      const sortIndex = nextSortIndexes[index];
      if (!sortIndex || entry.sortIndex === sortIndex) return;

      if (entry.type === "item") {
        itemEdits.push({ id: entry.id, sortIndex });
        return;
      }

      descriptorEdits.push({ id: entry.id, sortIndex });
    });

    return { type: "repair", itemEdits, descriptorEdits };
  }
}
